import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation } from 'react-i18next';
import { useLang } from '@/hooks/useLang';
import { buildLangPath, stripLangPrefix } from '@/utils/routeBuilders';

const FAQSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('orthodontics');
  const { lang } = useLang();

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();

    const scrollToContact = () => {
      const contactForm = document.getElementById('contact-form');
      if (contactForm) {
        const yOffset = -100; // Offset to account for fixed header
        const y =
          contactForm.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    };

    // Navigate to language-prefixed home and scroll to contact form.
    if (stripLangPrefix(window.location.pathname) !== '/') {
      navigate(buildLangPath(lang, '/'));
      setTimeout(scrollToContact, 100);
    } else {
      scrollToContact();
    }
  };

  const faqs = [
    {
      question: t('faq.items.0.question'),
      answer: t('faq.items.0.answer'),
    },
    {
      question: t('faq.items.1.question'),
      answer: t('faq.items.1.answer'),
    },
    {
      question: t('faq.items.2.question'),
      answer: t('faq.items.2.answer'),
    },
    {
      question: t('faq.items.3.question'),
      answer: t('faq.items.3.answer'),
    },
    {
      question: t('faq.items.4.question'),
      answer: t('faq.items.4.answer'),
    }
  ];

  return (
    <section id="faq" className="py-20 bg-dental-light/10 relative">
      {/* Background Elements */}
      <div className="absolute top-20 right-0 w-80 h-80 bg-dental/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-60 h-60 bg-dental-coral/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-gradient-primary inline-block relative after:content-[''] after:absolute after:-bottom-2 after:left-1/4 after:right-1/4 after:h-1 after:bg-dental-tertiary after:rounded-full reveal" data-direction="up">
            {t('faq.heading')}
          </h2>
          <p className="text-xl text-dental-dark/80 max-w-3xl mx-auto reveal" data-direction="up">
            {t('faq.intro')}
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto reveal" data-direction="up">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left font-display text-lg font-medium text-dental-dark hover:text-dental transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-dental-dark/70">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          <div className="mt-12 text-center">
            <p className="text-dental-dark/80 mb-4">
              {t('faq.bottomLine')}
            </p>
            <a 
              href={buildLangPath(lang, '/')}
              className="btn-primary inline-flex items-center justify-center"
              onClick={handleContactClick}
            >
              {t('faq.cta')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
